import { Box, MenuItem, Paper, TextField, Typography } from "@mui/material";

import "./QBR.scss";
import Loader from "../Loader";
import ExportQbrButton from "./ExportQbrButton";
import { CURRENT_QUARTER_KEY } from "./mapQbrReportToTerritoryOverview";
import TerritoryOverviewCard from "./TerritoryOverviewCard";
import { useQBRController } from "./useQBRController";

const ACCENT_ORANGE = "#E87722";

/** Quarter the report covers — current plus any snapshot quarters. */
function QuarterSelect({ options, value, onChange }) {
  return (
    <TextField
      select
      size="small"
      value={value}
      onChange={(event) => onChange?.(event.target.value)}
      SelectProps={{ inputProps: { "aria-label": "Report quarter" } }}
      sx={{
        width: 140,
        "& .MuiInputBase-root": {
          height: 32,
          borderRadius: "999px",
          bgcolor: "#fff",
          fontSize: 13,
          fontWeight: 600,
          color: "#202020",
        },
      }}
    >
      {options.map((option) => (
        <MenuItem key={option.key} value={option.key} sx={{ fontSize: 13 }}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

function QBR({ isActive = true }) {
  const {
    overview,
    quarterOptions,
    selectedQuarterKey,
    onQuarterChange,
    onExplore,
    isLoading,
    isError,
    hasData,
    productGroupLabel,
  } = useQBRController({ isActive });

  const showQuarterSelect = quarterOptions.length > 1;

  return (
    <>
      {isLoading && !hasData && <Loader />}
      <Box
        sx={{
          position: "sticky",
          top: "142px",
          backgroundColor: "#f9fdff",
          zIndex: 9,
          width: "100%",
          maxWidth: "1360px",
          m: "0 auto",
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.75,
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontSize: 22,
                  fontWeight: 600,
                  color: "#202020",
                  lineHeight: 1.1,
                }}
              >
                My Dashboard: QBR
              </Typography>
              {showQuarterSelect && (
                <QuarterSelect
                  options={quarterOptions}
                  value={selectedQuarterKey}
                  onChange={onQuarterChange}
                />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: ACCENT_ORANGE,
                lineHeight: 1.2,
              }}
            >
              Data autopopulated from RepAssist
            </Typography>
          </Box>
          <ExportQbrButton
            // Current-quarter view => undefined so the export omits ?quarter=
            // and the backend resolves to the live current quarter; prior
            // quarter => pass the compact id through.
            quarter={
              selectedQuarterKey === CURRENT_QUARTER_KEY
                ? undefined
                : selectedQuarterKey
            }
            productGroup={productGroupLabel}
            disabled={!hasData || isError}
          />
        </Box>
      </Box>

      <Paper elevation={3} className="drawer-paper-qbr">
        <Box
          sx={{
            p: 2,
            width: "100%",
            maxWidth: "1360px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <TerritoryOverviewCard
            overview={overview}
            onExplore={onExplore}
            // Current-quarter view => null so the PATCH omits ?quarter= and
            // the backend resolves to the live current quarter. Prior-quarter
            // view => pass the compact id through so Section 1 edits land in
            // the viewed quarter instead of leaking to the current one.
            quarterId={
              selectedQuarterKey === CURRENT_QUARTER_KEY
                ? null
                : selectedQuarterKey
            }
          />

          {/* Placeholder: Sections 2–4 (HCP Targets, Progressing Targets, New Targets) go here later. */}
        </Box>
      </Paper>
    </>
  );
}

export default QBR;
